import {
	BreadcrumbsStyled,
	ButtonLoadMore,
	ButtonLoadPrev,
	Description,
	GridPagination,
	List,
	ListContainer,
	Root,
	FiltersControlsContainer,
	ControlsWrapper,
	Row,
	ImageContainer,
	SortablesStyled,
	StickyStyled,
	ImageDescriptionContainer,
	Wrapper,
	LoadMoreContainer,
	ProgressBar,
	ProgressBarContainer,
} from './category.styled'
import { PAGE_PATH_HOME } from '@bluheadless/ui-logic/src/constants'
import { useCatalogMergeItemsWithBanners } from '@bluheadless/ui-logic/src/hooks/catalog-navigator/useCatalogMergeItemsWithBanners'
import { useCatalogNavigator } from '@bluheadless/ui-logic/src/hooks/catalog-navigator/useCatalogNavigator'
import { useCategories } from '@bluheadless/ui-logic/src/hooks/category/useCategories'
import { useConfig } from '@bluheadless/ui-logic/src/providers/config'
import { usePagination } from '@bluheadless/ui-logic/src/hooks/usePagination'
import { Box, Grid } from '@mui/material'
import { FormattedMessage, useIntl } from 'react-intl'
import { number, string, bool, array, object, oneOf, oneOfType } from 'prop-types'
import TileConnected from '../../molecules/tile/connected/tile-connected'
import { useEffect, useRef, useMemo } from 'react'
import { useInView } from 'react-intersection-observer'
import { useTheme } from '@mui/system'
import AdvancedGrid from '../../molecules/advanced-grid'
import { cx } from '@emotion/css'
import ComparableProducts from '../../organisms/comparable-products'
import DynamicAreas from '../../particles/dynamic-areas'
import FiltersDrawer from '../../molecules/filters/drawer/filters-drawer'
import FiltersInline from '../../molecules/filters/inline/filters-inline'
import Image from '../../particles/image'
import Pagination from '../../molecules/pagination'
import RichContent from '../../particles/rich-content'
import useMediaQuery from '@mui/material/useMediaQuery'
import ViewMode from '../../molecules/view-mode'
import Progress from '../../atoms/progress'
import Heading from './heading'
import { useSortCollection } from '@bluheadless/ui-logic/src/hooks/useSortCollection'
import useCategoryScrollRestoration from '@bluheadless/ui-logic/src/hooks/useCategoryScrollRestoration'
import CategoryProvider from '@bluheadless/ui-logic/src/providers/category/category'
import {
	getChildrenCategoriesWhere,
	getIsChildrenCategoriesEnabled,
} from '@bluheadless/ui-logic/src/catalog/children-categories'
import {
	getSiblingsCategoriesWhere,
	getIsSiblingsCategoriesEnabled,
} from '@bluheadless/ui-logic/src/catalog/siblings-categories'
import { usePageContext } from '@bluheadless/ui-logic/src/providers/page/page'
import RichSnippetCategory from '../../particles/rich-snippets/category'
import { ChildrenCategories, SiblingsCategories } from '@bluheadless/ui/src/page-components/category/heading.styled'
import Link from '@bluheadless/ui/src/molecules/link'
import { useClerkScript } from '@bluheadless/ui-logic/src/hooks/clerk/useClerkScript'
import Typography from '../../atoms/typography'

const CategoryPageComponent = ({
	entityId,
	className,
	dynamicAreas,
	filters: defaultFilters,
	baseWhere,
	where: defaultWhere,
	order: defaultOrder,
	sort: defaultSort,
	page: defaultPage,
	offset: defaultOffset,
	limit: defaultLimit,
	lastPage: defaultLastPage,
	viewModeNavigator,
	viewModeDefault,
	hideFilters,
	hideSortby,
	emotionalProducts,
	layout,
	...props
}) => {
	const {
		name,
		seoName,
		childrenIds,
		tileVariant: categoryTileVariant,
		description,
		image,
		imageMobile,
		breadcrumbs,
		headingVariant: categoryHeadingVariant,
		viewModeEnabled: categoryViewModeEnabled,
		siblingsCategoriesEnabled: categorySiblingsCategoriesEnabled,
		childrenCategoriesEnabled: categoryChildrenCategoriesEnabled,
	} = props

	useClerkScript()

	const { formatMessage } = useIntl()
	const theme = useTheme()
	const mdUp = useMediaQuery(theme.breakpoints.up('md'))
	const rowRef = useRef(null)
	const listingRef = useRef(null)
	const rootRef = useRef(null)

	const {
		catalog: {
			categories: {
				heading: { variant: configHeadingVariant },
				filters: {
					enabled: enabledFilters,
					variant: filtersVariant,
					showProductsCount: { parent: parentCount, children: childrenCount },
					priceVariant: priceVariant,
					showHideFiltersButton,
				},
				sortBy: { enabled: enabledSortBy },
				columnsDefault,
				columnsAlternate,
				viewMode: { enabled: configViewModeEnabled },
				tileVariant: configTileVariant,
				pageSize,
				pagination,
				infiniteScroll: { enabled: infiniteScrollEnabled, showMore: infiniteScrollShowMore },
				childrenCategories: { enabled: childrenCategoriesEnabledConfig },
				siblingsCategories: { enabled: siblingsCategoriesEnabledConfig },
			},
			products: {
				comparable: { enabled: comparableEnabled },
			},
		},
	} = useConfig()

	const [inViewRef, inView] = useInView({
		threshold: 0,
		rootMargin: '400px',
		triggerOnce: false,
		skip: !(infiniteScrollEnabled && !infiniteScrollShowMore),
	})

	const hasSiblingsCategories = categorySiblingsCategoriesEnabled && siblingsCategoriesEnabledConfig
	const hasChildrenCategories = categoryChildrenCategoriesEnabled && childrenCategoriesEnabledConfig

	const childrenCategoriesEnabled = useMemo(
		() => getIsChildrenCategoriesEnabled(props, hasChildrenCategories),
		[props, hasChildrenCategories]
	)
	const childrenCategoriesWhere = useMemo(() => getChildrenCategoriesWhere(props), [props])

	const { categories: childrenCategories } = useCategories({
		where: childrenCategoriesWhere,
		order: 'position',
		paused: !childrenCategoriesEnabled,
	})

	const childrenCategoriesSorted = useSortCollection({
		collection: childrenCategories,
		sortedValues: childrenIds,
	})

	const siblingsCategoriesEnabled = useMemo(
		() => getIsSiblingsCategoriesEnabled(props, hasSiblingsCategories),
		[props, hasSiblingsCategories]
	)

	const siblingsCategoriesWhere = useMemo(() => getSiblingsCategoriesWhere(props), [props])

	const { categories: siblingsCategories } = useCategories({
		where: siblingsCategoriesWhere,
		order: 'position',
		paused: !siblingsCategoriesEnabled,
	})

	// useCategoryScrollRestoration must be above usePagination
	// otherwise pagination will load another page (if in view)
	// and scroll restoration will not work.
	const { onProductClick, scrollPosition, productScrollRef } = useCategoryScrollRestoration({
		categoryId: entityId ?? 'global-catalog',
	})

	const { entityType: pageType } = usePageContext()

	const {
		currentPage,
		onLoadPrev,
		onLoadNext,
		onLoadPage,
		onLastPageChange,
		prevPage,
		lastPage,
		lastPageReached,
		nextPage,
	} = usePagination({
		defaultLastPage: Number.isFinite(defaultLastPage) ? defaultLastPage : defaultPage,
		defaultCurrentPage: defaultPage,
		updateRouterMode: infiniteScrollEnabled ? 'replace' : 'push',
	})

	const {
		loading,
		loadingProducts,
		activeFilters,
		filters,
		sort,
		sortables,
		productsCount,
		products,
		onSortChange,
		onApplyFilter,
		onDeleteFilter,
		onDeleteAllFilters,
		sortIsDefault,
		hasAppliedFilters,
		viewMode,
		onViewModeChange,
	} = useCatalogNavigator({
		categoryId: entityId,
		defaultFilters,
		baseWhere,
		defaultWhere,
		defaultOrder,
		defaultSort,
		defaultOffset,
		defaultLimit,
		page: currentPage,
		defaultPageSize: pageSize,
		pagination,
		onLastPageChange,
		onLoadPage,
		defaultLastPage,
		infinite: infiniteScrollEnabled,
		viewModeNavigator,
		viewModeDefault,
		scrollingRef: rootRef,
		pageContext: pageType,
		pageId: entityId,
	})

	useEffect(() => {
		// do not trigger if contents are already loading.
		// prevents infinite scroll from triggering on page load
		if (inView && !loadingProducts) {
			onLoadNext()
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [inView, loadingProducts])

	const listing = useCatalogMergeItemsWithBanners({
		items: products,
		banners: dynamicAreas,
		categoryId: entityId,
		pageStart: prevPage + 1,
		pageEnd: nextPage - 1,
		defaultPageSize: pageSize,
		pagination,
		skip: !sortIsDefault || hasAppliedFilters,
		emotionalProducts,
	})

	const breadcrumbsItems = [{ title: formatMessage({ id: 'breadcrumbs_home' }), url: PAGE_PATH_HOME }].concat(
		breadcrumbs?.length > 0
			? breadcrumbs?.map(({ label, path }) => {
					return { title: label, url: path }
			  })
			: [{ title: seoName || name }] || []
	)
	const categoryIsEmpty = !listing?.length > 0 && products !== undefined
	const showFilters = !hideFilters && enabledFilters && filters?.length > 0
	const showSortBy = !hideSortby && enabledSortBy && sortables?.length > 0
	const headingVariant = categoryHeadingVariant?.length > 0 ? categoryHeadingVariant : configHeadingVariant
	const sortableItems = sortables?.map(({ code, label, direction }) => ({
		code,
		label: formatMessage({
			id: `sortable_${code}_${direction}`,
			defaultMessage: `${label} ${direction}`,
		}),
		direction,
	}))

	const headingComponent = (
		<Heading
			variant={headingVariant}
			title={seoName || name}
			image={image}
			imageMobile={imageMobile}
			description={description}
			productsCount={productsCount}
			childrenCategories={childrenCategoriesSorted}
			siblingsCategories={siblingsCategories}
			entityId={entityId}
		/>
	)

	const breakpointsColumnsDefault = Object.keys(columnsDefault).map((breakpoint) => {
		return {
			[breakpoint]: {
				columns: columnsDefault[breakpoint],
			},
		}
	})

	const transformedColumnsDefault = breakpointsColumnsDefault.reduce((previousValue, currentValue) => ({
		...previousValue,
		...currentValue,
	}))

	const breakpointsColumnsAlternate = Object.keys(columnsAlternate).map((breakpoint) => {
		return {
			[breakpoint]: {
				columns: columnsAlternate[breakpoint],
			},
		}
	})

	const transformedColumnsAlternate = breakpointsColumnsAlternate.reduce((previousValue, currentValue) => ({
		...previousValue,
		...currentValue,
	}))

	const childrenCatRef = useRef()
	const siblingsCatRef = useRef()

	useEffect(() => {
		if (hasChildrenCategories && childrenCategories?.length && childrenCatRef?.current) {
			const childrenWrapper = childrenCatRef?.current
			const activeItem = childrenCatRef?.current?.querySelector('.active')

			if (activeItem) {
				const menuRect = childrenWrapper.getBoundingClientRect()
				const activeRect = activeItem.getBoundingClientRect()

				const scrollLeft =
					activeRect.left - menuRect.left - childrenWrapper.offsetWidth / 2 + activeItem.offsetWidth / 2

				childrenWrapper.scrollLeft += scrollLeft
			}

			document.documentElement.style.setProperty(
				'--children-categories-height',
				`${childrenCatRef?.current?.offsetHeight}px`
			)
		}
		if (hasSiblingsCategories && siblingsCategories?.length && siblingsCatRef?.current) {
			const siblingsWrapper = siblingsCatRef?.current
			const activeItem = siblingsCatRef?.current?.querySelector('.active')

			if (activeItem) {
				const menuRect = siblingsWrapper.getBoundingClientRect()
				const activeRect = activeItem.getBoundingClientRect()

				const scrollLeft =
					activeRect.left - menuRect.left - siblingsWrapper.offsetWidth / 2 + activeItem.offsetWidth / 2

				siblingsWrapper.scrollLeft += scrollLeft
			}

			document.documentElement.style.setProperty(
				'--children-categories-height',
				`${siblingsCatRef?.current?.offsetHeight}px`
			)
		}
		return () => {
			document.documentElement.style.setProperty('--children-categories-height', `0px`)
		}
	}, [childrenCategories, hasChildrenCategories, hasSiblingsCategories, siblingsCatRef, siblingsCategories])

	const viewModeEnabled = categoryViewModeEnabled && configViewModeEnabled

	return (
		<Root className={cx('Category-root', className)} ref={rootRef}>
			<RichSnippetCategory items={listing} />
			{headingVariant == 'banner' && headingComponent}
			<CategoryProvider
				category={{
					id: entityId,
					...props,
				}}
			>
				<DynamicAreas areas={dynamicAreas} container="top" />
				{!categoryIsEmpty && filtersVariant === 'inline' && headingVariant == 'default' && headingComponent}
				{filtersVariant === 'sidebar' && headingVariant != 'banner' && headingComponent}
				{!categoryIsEmpty && headingVariant == 'default' && (description || image) && (
					<ImageDescriptionContainer>
						{description?.length > 0 ? (
							<Description>
								<RichContent content={description} />
							</Description>
						) : null}

						{image?.length > 0 ? (
							<ImageContainer>
								<Image src={image} alt={seoName || name} />
							</ImageContainer>
						) : null}
					</ImageDescriptionContainer>
				)}
				<StickyStyled
					enabled={viewModeEnabled || showFilters || showSortBy}
					bottomBoundary="#category-product-list"
					offset={0}
					show={
						(!categoryIsEmpty && mdUp && (viewModeEnabled || showFilters || showSortBy)) ||
						(hasChildrenCategories && childrenCategoriesSorted?.length) ||
						(hasSiblingsCategories && siblingsCategories?.length)
					}
				>
					<Row ref={rowRef}>
						<Wrapper>
							{hasChildrenCategories && childrenCategoriesSorted?.length > 0 && (
								<ChildrenCategories ref={childrenCatRef} justifyContent="flex-start">
									{childrenCategoriesSorted.map(({ name, path, id }) => (
										<Link key={id} href={path} underline="none" className={cx(id === entityId && 'active')}>
											{name}
										</Link>
									))}
								</ChildrenCategories>
							)}

							{hasSiblingsCategories && siblingsCategories?.length > 0 && (
								<SiblingsCategories ref={siblingsCatRef} justifyContent="flex-start">
									{siblingsCategories.map(({ name, path, id }) => (
										<Link
											key={id}
											href={id !== entityId ? path : null}
											underline="none"
											className={cx(id === entityId && 'active')}
										>
											{name}
										</Link>
									))}
								</SiblingsCategories>
							)}

							{!categoryIsEmpty ? (
								<FiltersControlsContainer filtersVariant={filtersVariant}>
									{filtersVariant === 'inline' ? (
										<FiltersInline
											className="filters-inline"
											onApplyFilter={onApplyFilter}
											onDeleteActiveFilter={onDeleteFilter}
											onDeleteAllFilter={onDeleteAllFilters}
											onApplySortable={onSortChange}
											items={showFilters ? filters : []}
											sortables={showSortBy && sortableItems}
											sortablesProps={{
												value: JSON.stringify(sort),
												loading: loading,
											}}
											viewMode={{
												enabled: viewModeEnabled,
												variant: viewMode,
												onClick: onViewModeChange,
											}}
											loading={loading}
											priceVariant={priceVariant}
											showCountOnOption={childrenCount}
											filterMaxWidth=""
										/>
									) : (
										<ControlsWrapper filtersVariant={filtersVariant}>
											{viewModeEnabled && <ViewMode variant={viewMode} onClick={onViewModeChange} />}

											{showFilters && showHideFiltersButton && (
												<FiltersDrawer
													buttonProps={{ className: activeFilters?.length > 0 ? '_active' : null, StartIcon: null }}
													buttonLabel={
														formatMessage({ id: 'filter_link_opener' }) +
														(activeFilters?.length > 0 ? ` (${activeFilters.length})` : '')
													}
													onResetFilters={onDeleteAllFilters}
													filtersProps={{
														items: filters,
														onApplyFilter: onApplyFilter,
														onDeleteActiveFilter: onDeleteFilter,
														onDeleteAllFilter: onDeleteAllFilters,
														showCountOnOption: childrenCount,
														showCountOnTitle: parentCount,
													}}
													sortablesProps={{
														items: sortableItems,
														onApplySortable: onSortChange,
														value: JSON.stringify(sort),
														loading: loading,
													}}
												/>
											)}

											{showSortBy && (
												<SortablesStyled
													items={sortableItems}
													onChange={onSortChange}
													value={JSON.stringify(sort)}
													placeholder={false}
													IconComponent={null}
												/>
											)}
										</ControlsWrapper>
									)}
								</FiltersControlsContainer>
							) : null}
						</Wrapper>
					</Row>
				</StickyStyled>
				<DynamicAreas areas={dynamicAreas} container="middle" />
				{categoryIsEmpty ? (
					<Box mt={4} mb={8} textAlign="center" display="flex" alignItems="center" flexDirection="column" gap={4}>
						{filtersVariant === 'inline' && headingVariant == 'default' && headingComponent}
						{/* <Notification message={formatMessage({ id: 'category_no_products' })} severity="info" /> */}
						{formatMessage({ id: 'category_no_products_found' })}
					</Box>
				) : (
					<ListContainer template={layout.template}>
						<List filtersVariant={filtersVariant} ref={listingRef} id="category-product-list">
							{infiniteScrollEnabled && prevPage > 0 && (
								<Grid item xs={12} textAlign="center">
									<ButtonLoadPrev onClick={onLoadPrev} disabled={loading} loading={loading}>
										<FormattedMessage id="load_prev" />
									</ButtonLoadPrev>
								</Grid>
							)}

							<AdvancedGrid
								responsiveProps={viewMode === 'default' ? transformedColumnsDefault : transformedColumnsAlternate}
								items={listing.map((item, index) => ({
									id: `${item.isListingBanner ? 'banner_' : 'product_'}${item.id}`,
									contentColumns: item?.contentColumns || 1,
									contentRows: item?.contentRows || 1,
									content: item.isListingBanner ? (
										<RichContent key={item.id} content={item.content} />
									) : (
										<TileConnected
											position={index}
											product={item}
											variant={categoryTileVariant || configTileVariant}
											// ⬇ these props are used only for the scroll restoration ⬇
											scrollingRef={scrollPosition?.productId === item.id ? productScrollRef : null}
											onClick={() =>
												onProductClick({
													page: item.__page,
													productId: item.id,
												})
											}
											modeView={viewMode}
											activeFilters={activeFilters}
										/>
									),
								}))}
								columnsGap={2}
								rowsGap={mdUp ? 2 : 28}
							/>
							{lastPage > 1 && !infiniteScrollEnabled && (
								<GridPagination item xs={12}>
									<Pagination
										count={lastPage}
										page={currentPage}
										onChange={(e, page) => onLoadPage(page, listingRef)}
										disabled={loading}
										hideNextButton={false}
										hidePrevButton={false}
										showFirstButton={false}
										showLastButton={false}
									/>
								</GridPagination>
							)}
							{infiniteScrollEnabled && infiniteScrollShowMore && !lastPageReached && (
								<LoadMoreContainer item xs={12} textAlign="center">
									<Typography component="p" variant="h4">
										{formatMessage({ id: 'products_viewed' }, { current: listing.length, total: productsCount })}
									</Typography>
									<ProgressBarContainer>
										<ProgressBar sx={{ width: (listing.length * 100) / productsCount + '%' }} />
									</ProgressBarContainer>
									<ButtonLoadMore onClick={onLoadNext} disabled={loading} loading={loading}>
										{formatMessage({ id: 'load_more' }, { pageSize: pageSize })}
									</ButtonLoadMore>
								</LoadMoreContainer>
							)}
							{infiniteScrollEnabled && !infiniteScrollShowMore && !lastPageReached && !loadingProducts && (
								<Grid item xs={12} ref={inViewRef}></Grid>
							)}
							{loadingProducts && infiniteScrollEnabled && !infiniteScrollShowMore && (
								<Grid item xs={12} textAlign="center" sx={{ padding: '25px 0px' }}>
									<Progress />
								</Grid>
							)}
						</List>
					</ListContainer>
				)}
				<DynamicAreas areas={dynamicAreas} container="bottom" />
			</CategoryProvider>
			<BreadcrumbsStyled items={breadcrumbsItems} activeLinkProps={{ underline: 'none', variant: 'body2' }} />
			{comparableEnabled && <ComparableProducts />}
		</Root>
	)
}

CategoryPageComponent.propTypes = {
	baseWhere: object,
	breadcrumbs: array,
	childrenIds: array,
	description: string,
	dynamicAreas: array,
	entityId: number,
	filters: array,
	headingVariant: oneOfType([oneOf(['hidden', 'default', 'banner']), string]),
	hideFilters: bool,
	hideSortby: bool,
	image: string,
	lastPage: number,
	limit: number,
	name: string,
	offset: number,
	order: array,
	page: number,
	seoName: oneOfType([string, object]),
	sort: object,
	tileVariant: string,
	viewMode: string,
	where: object,
	rootCategoryLevel: number,
}

export default CategoryPageComponent
